/**
 * Dialog — React (web) implementation.
 *
 * Built on the native `<dialog>` element opened with `showModal()`: the
 * browser puts it in the top layer, makes the page behind it inert and draws
 * the `::backdrop` scrim, so no z-index or portal is needed. The component
 * stays controlled — Escape and the close button ask the host to close via
 * `onClose`, and only a change to `open` actually closes it.
 *
 * Focus moves to the close button on open, Tab and Shift+Tab wrap inside the
 * dialog, and focus returns to whatever was focused before it opened.
 */

import React, { useEffect, useId, useRef, type ReactNode } from 'react';
import { Button } from '../Button/Button.web';
import { Icon } from '../Icon/Icon.web';
import { TextButton } from '../TextButton/TextButton.web';
import { ensureFocusVisibleStyle, ensureStyle } from '../../elements/focusStyle.web';
import {
  DIALOG_ACTIONS_SPACER,
  DIALOG_BACKDROP,
  DIALOG_BACKGROUND,
  DIALOG_BODY_TYPOGRAPHY,
  DIALOG_CENTERED_GAP,
  DIALOG_CENTERED_MAX_WIDTH,
  DIALOG_CENTERED_PADDING,
  DIALOG_CENTERED_RADIUS,
  DIALOG_CENTERED_SHADOW,
  DIALOG_CLOSE_BACKGROUND,
  DIALOG_CLOSE_ICON,
  DIALOG_CLOSE_ICON_COLOR,
  DIALOG_CLOSE_ICON_SIZE,
  DIALOG_CLOSE_RADIUS,
  DIALOG_CLOSE_SIZE,
  DIALOG_GRABBER_COLOR,
  DIALOG_GRABBER_HEIGHT,
  DIALOG_GRABBER_RADIUS,
  DIALOG_GRABBER_WIDTH,
  DIALOG_HEADER_GAP,
  DIALOG_SHEET_GAP,
  DIALOG_SHEET_PADDING_BOTTOM,
  DIALOG_SHEET_PADDING_HORIZONTAL,
  DIALOG_SHEET_PADDING_TOP,
  DIALOG_SHEET_RADIUS,
  DIALOG_TEXT_COLOR,
  DIALOG_TITLE_TYPOGRAPHY,
  type DialogProps,
} from './Dialog';

export interface WebDialogProps extends DialogProps {
  onClose?: () => void;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  /** Body content, shown between the header and the actions. */
  children?: ReactNode;
  'data-testid'?: string;
}

const DIALOG_STYLE_ID = 'galapagos-dialog';
const DIALOG_SELECTOR = '[data-galapagos-dialog="true"]';
const CLOSE_FOCUS_STYLE_ID = 'galapagos-dialog-close-focus-visible';
const CLOSE_SELECTOR = '[data-galapagos-dialog-close="true"]';

/** Focusable and actually reachable by Tab: not disabled (directly or by a
 *  disabled fieldset), not inside a hidden or inert subtree, not hidden by
 *  CSS or a closed `<details>`, and not tabindex -1. */
function isTabbable(element: HTMLElement): boolean {
  if (element.matches(':disabled')) return false;
  if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  // `checkVisibility` covers display:none ancestors, visibility:hidden and
  // closed <details>; an environment without it keeps the checks above.
  if (
    typeof element.checkVisibility === 'function' &&
    !element.checkVisibility({ checkVisibilityCSS: true })
  ) {
    return false;
  }
  return element.tabIndex >= 0;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function ensureDialogStyle(): void {
  ensureStyle(
    DIALOG_STYLE_ID,
    `${DIALOG_SELECTOR}::backdrop { background-color: ${DIALOG_BACKDROP}; }`,
  );
}

const CENTERED_SHADOW = `${DIALOG_CENTERED_SHADOW.offset.width}px ${DIALOG_CENTERED_SHADOW.offset.height}px ${DIALOG_CENTERED_SHADOW.radius}px ${DIALOG_CENTERED_SHADOW.spread}px ${DIALOG_CENTERED_SHADOW.color}`;

export function Dialog({
  open = false,
  presentation = 'centered',
  title,
  accessibilityLabel,
  closeLabel,
  primaryActionLabel,
  secondaryActionLabel,
  testID,
  onClose,
  onPrimaryAction,
  onSecondaryAction,
  children,
  actionId = 'ui.dialog.dismiss',
  primaryActionId = 'ui.dialog.primary',
  secondaryActionId = 'ui.dialog.secondary',
  'data-testid': dataTestId,
}: WebDialogProps) {
  ensureDialogStyle();
  ensureFocusVisibleStyle(CLOSE_FOCUS_STYLE_ID, [CLOSE_SELECTOR]);

  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const testId = dataTestId ?? testID;
  const isSheet = presentation === 'sheet';

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      returnFocusRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      // An environment without the modal dialog API (an old browser, a test
      // DOM) still shows the dialog, just without the inert page behind it.
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      closeRef.current?.focus();
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    } else if (!open) {
      // Already closed by the browser itself; it restored focus, so drop ours.
      returnFocusRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    const returnFocus = returnFocusRef;
    return () => {
      // Unmounted while open: hand focus back rather than dropping it on body.
      if (dialog?.open) returnFocus.current?.focus();
    };
  }, []);

  const handleCancel: React.ReactEventHandler<HTMLDialogElement> = (event) => {
    // Escape asks the host to close; the dialog only closes when `open` flips.
    event.preventDefault();
    onClose?.();
  };

  const handleNativeClose: React.ReactEventHandler<HTMLDialogElement> = () => {
    // The browser closed the dialog itself (it can skip `cancel`, e.g. a
    // repeated Escape); tell the host so `open` follows.
    if (open) onClose?.();
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDialogElement> = (event) => {
    if (event.key !== 'Tab') return;
    const focusables = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter(isTabbable);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const dialogStyle: React.CSSProperties = isSheet
    ? {
        // A modal <dialog> sits in the browser's top layer and takes no room in
        // its parent's layout; this margin only pins the sheet to the bottom
        // edge of the viewport, so it is not the layout margin ADR-020 Rule 2
        // guards against.
        margin: 'auto 0 0',
        width: '100%',
        maxWidth: '100%',
        padding: 0,
        border: 'none',
        backgroundColor: DIALOG_BACKGROUND,
        borderRadius: `${DIALOG_SHEET_RADIUS}px ${DIALOG_SHEET_RADIUS}px 0 0`,
      }
    : {
        width: '100%',
        maxWidth: DIALOG_CENTERED_MAX_WIDTH,
        padding: 0,
        border: 'none',
        backgroundColor: DIALOG_BACKGROUND,
        borderRadius: DIALOG_CENTERED_RADIUS,
        boxShadow: CENTERED_SHADOW,
      };

  const contentStyle: React.CSSProperties = isSheet
    ? {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: DIALOG_SHEET_GAP,
        paddingTop: DIALOG_SHEET_PADDING_TOP,
        paddingBottom: DIALOG_SHEET_PADDING_BOTTOM,
        paddingLeft: DIALOG_SHEET_PADDING_HORIZONTAL,
        paddingRight: DIALOG_SHEET_PADDING_HORIZONTAL,
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: DIALOG_CENTERED_GAP,
        padding: DIALOG_CENTERED_PADDING,
      };

  const titleStyle: React.CSSProperties = {
    flex: 1,
    margin: 0,
    color: DIALOG_TEXT_COLOR,
    fontFamily: DIALOG_TITLE_TYPOGRAPHY.fontFamily,
    fontWeight: DIALOG_TITLE_TYPOGRAPHY.fontWeight,
    fontSize: DIALOG_TITLE_TYPOGRAPHY.fontSize,
    lineHeight: `${DIALOG_TITLE_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: DIALOG_TITLE_TYPOGRAPHY.letterSpacing,
  };

  const bodyStyle: React.CSSProperties = {
    alignSelf: 'stretch',
    color: DIALOG_TEXT_COLOR,
    fontFamily: DIALOG_BODY_TYPOGRAPHY.fontFamily,
    fontWeight: DIALOG_BODY_TYPOGRAPHY.fontWeight,
    fontSize: DIALOG_BODY_TYPOGRAPHY.fontSize,
    lineHeight: `${DIALOG_BODY_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: DIALOG_BODY_TYPOGRAPHY.letterSpacing,
  };

  const closeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: DIALOG_CLOSE_SIZE,
    height: DIALOG_CLOSE_SIZE,
    padding: 0,
    border: 'none',
    borderRadius: DIALOG_CLOSE_RADIUS,
    backgroundColor: DIALOG_CLOSE_BACKGROUND,
    cursor: 'pointer',
  };

  const hasActions = Boolean(primaryActionLabel || secondaryActionLabel);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={title ? titleId : undefined}
      aria-label={title ? undefined : accessibilityLabel}
      onCancel={handleCancel}
      onClose={handleNativeClose}
      onKeyDown={handleKeyDown}
      style={dialogStyle}
      data-galapagos-dialog="true"
      data-presentation={presentation}
      data-testid={testId}
    >
      <div style={contentStyle}>
        {isSheet ? (
          <span
            aria-hidden
            data-testid={testId ? `${testId}-grabber` : undefined}
            style={{
              display: 'block',
              width: DIALOG_GRABBER_WIDTH,
              height: DIALOG_GRABBER_HEIGHT,
              borderRadius: DIALOG_GRABBER_RADIUS,
              backgroundColor: DIALOG_GRABBER_COLOR,
            }}
          />
        ) : null}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: DIALOG_HEADER_GAP,
            alignSelf: 'stretch',
          }}
        >
          {title ? (
            <h2 id={titleId} style={titleStyle}>
              {title}
            </h2>
          ) : (
            <span style={{ flex: 1 }} />
          )}
          <button
            ref={closeRef}
            type="button"
            aria-label={closeLabel}
            onClick={() => onClose?.()}
            style={closeStyle}
            data-galapagos-dialog-close="true"
            data-analytics-action={actionId}
            data-testid={testId ? `${testId}-close` : undefined}
          >
            <Icon
              name={DIALOG_CLOSE_ICON}
              size={DIALOG_CLOSE_ICON_SIZE}
              color={DIALOG_CLOSE_ICON_COLOR}
              aria-hidden
            />
          </button>
        </div>
        {children ? (
          <div style={bodyStyle} data-testid={testId ? `${testId}-body` : undefined}>
            {children}
          </div>
        ) : null}
        {hasActions ? (
          <div
            style={{
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              gap: isSheet ? DIALOG_SHEET_GAP : DIALOG_CENTERED_GAP,
              paddingTop: DIALOG_ACTIONS_SPACER,
            }}
          >
            {primaryActionLabel ? (
              <Button
                size="L"
                type="Primary"
                width="fill"
                label={primaryActionLabel}
                actionId={primaryActionId}
                onPress={onPrimaryAction}
                data-testid={testId ? `${testId}-primary` : undefined}
              />
            ) : null}
            {secondaryActionLabel ? (
              <TextButton
                type="Button"
                label={secondaryActionLabel}
                actionId={secondaryActionId}
                onPress={onSecondaryAction}
                data-testid={testId ? `${testId}-secondary` : undefined}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </dialog>
  );
}
